import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { LabelPrintModal } from "@/components/printing/LabelPrintModal";
import type { ProductLabelData } from "@/components/supply/ProductLabelPrintSheet.types";
import { ModuleConsoleFooter } from "@/components/composition/ModuleConsoleFooter";
import { PageHeader } from "@/components/PageHeader";
import { TableSkeleton } from "@/components/skeleton/TableSkeleton";
import { TablePagination } from "@/components/TablePagination";
import { useClientPagination } from "@/hooks/useClientPagination";
import { t } from "@/core/composition/lexicon";
import { pharmacyInventoryApi } from "@/core/inventory/inventory-api";
import { IscApiError } from "@/core/inventory/isc-client";
import {
  depotSupplyRoutes,
  STOCK_DEPOTS,
  type StockDepotId,
} from "@/core/inventory/stock-depots";
import type { StockProduct } from "@/core/pharmacy/types";
import { isCode39Compatible } from "@/core/printing/code39-barcode";
import { routes } from "@/router/all_routes";

interface ArticleRow {
  product: StockProduct;
  onHand: number;
  reserved: number;
  available: number;
}

interface SupplyArticlesPageProps {
  depot: StockDepotId;
}

function isApiOfflineError(err: unknown): boolean {
  return err instanceof IscApiError && err.status === 0;
}

export function SupplyArticlesPage({ depot }: SupplyArticlesPageProps) {
  const depotConfig = STOCK_DEPOTS[depot];
  const depotRoutes = depotSupplyRoutes(depot);
  const otherDepot: StockDepotId = depot === "pharmacy" ? "consumables" : "pharmacy";
  const otherRoutes = depotSupplyRoutes(otherDepot);

  const [rows, setRows] = useState<ArticleRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [printLabel, setPrintLabel] = useState<ProductLabelData | null>(null);
  const [printOpen, setPrintOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const storeId = await pharmacyInventoryApi.getDepotStoreId(depot);
      const [products, balances] = await Promise.all([
        pharmacyInventoryApi.listProductsForDepot(depot),
        storeId ? pharmacyInventoryApi.listStockBalancesForStore(storeId) : [],
      ]);
      const map = new Map(
        balances.map((b) => [
          b.productId,
          { onHand: b.quantityOnHand, reserved: b.quantityReserved, available: b.quantityAvailable },
        ]),
      );
      setRows(
        products
          .filter((p) => p.status === "ACTIVE")
          .map((product) => {
            const bal = map.get(product.id) ?? { onHand: 0, reserved: 0, available: 0 };
            return { product, ...bal };
          })
          .sort((a, b) => a.product.label.localeCompare(b.product.label, "fr")),
      );
    } catch (err) {
      setError(
        isApiOfflineError(err)
          ? "API stock inaccessible."
          : err instanceof Error
            ? err.message
            : "Chargement impossible.",
      );
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [depot]);

  useEffect(() => {
    void load();
  }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      ({ product }) =>
        product.label.toLowerCase().includes(q) ||
        product.sku.toLowerCase().includes(q) ||
        (product.barcode?.toLowerCase().includes(q) ?? false),
    );
  }, [rows, search]);

  const pagination = useClientPagination(filtered, [search], 25);
  const { pagedItems } = pagination;

  return (
    <div className="ph-dashboard">
      <PageHeader
        title={`${depotConfig.label} — ${t("supply.articles")}`}
        description={`Dépôt ${depotConfig.storeCode} — catalogue et soldes isolés du dépôt ${STOCK_DEPOTS[otherDepot].shortLabel.toLowerCase()}.`}
        actions={
          <div className="d-flex gap-2 flex-wrap ph-page-header-actions">
            <Link to={depotRoutes.receipts} className="btn btn-primary btn-sm">
              {t("supply.receipts")}
            </Link>
            <Link to={depotRoutes.levels} className="btn btn-outline-secondary btn-sm">
              {t("supply.levels")}
            </Link>
            <Link to={otherRoutes.articles} className="btn btn-outline-secondary btn-sm">
              {STOCK_DEPOTS[otherDepot].shortLabel}
            </Link>
            {depot === "pharmacy" && (
              <Link to={routes.pharmacyStockArticles} className="btn btn-outline-secondary btn-sm">
                Prix officine
              </Link>
            )}
          </div>
        }
      />

      {error && <div className="alert alert-warning py-2 mb-3">{error}</div>}

      <div className="ph-invoice-sheet mb-3">
        <input
          type="search"
          className="form-control form-control-sm w-100"
          placeholder="Rechercher un article…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      <div className="ph-dash-card">
        {loading ? (
          <TableSkeleton rows={10} columns={8} />
        ) : (
          <div className="table-responsive ph-diag-table">
            <table className="table table-sm ph-dash-table align-middle mb-0">
              <thead>
                <tr>
                  <th>SKU</th>
                  <th>Libellé</th>
                  <th>Unité</th>
                  <th className="text-end">{t("stock.onHand")}</th>
                  <th className="text-end">{t("stock.reserved")}</th>
                  <th className="text-end">{t("stock.available")}</th>
                  <th className="text-end">Étiquette</th>
                </tr>
              </thead>
              <tbody>
                {pagedItems.map(({ product, onHand, reserved, available }) => (
                  <tr key={product.id} className="ph-diag-row">
                    <td data-label="SKU">
                      <code className="small">{product.sku}</code>
                    </td>
                    <td data-label="Libellé" className="fw-medium">
                      {product.label}
                    </td>
                    <td data-label="Unité" className="small">
                      {product.unit}
                    </td>
                    <td data-label={t("stock.onHand")} className="text-end">
                      {onHand}
                    </td>
                    <td data-label={t("stock.reserved")} className="text-end text-muted">
                      {reserved}
                    </td>
                    <td data-label={t("stock.available")} className="text-end">
                      {available}
                    </td>
                    <td data-label="Étiquette" className="text-end">
                      <button
                        type="button"
                        className="btn btn-outline-primary btn-sm"
                        disabled={!isCode39Compatible((product.barcode ?? product.sku).trim())}
                        onClick={() => {
                          const barcode = (product.barcode ?? product.sku).trim();
                          setPrintLabel({
                            productName: product.label,
                            barcode,
                            sku: product.sku,
                            quantity: 1,
                            unit: product.unit,
                          });
                          setPrintOpen(true);
                        }}
                      >
                        Imprimer
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <TablePagination
              page={pagination.page}
              pageSize={pagination.pageSize}
              totalItems={pagination.totalItems}
              onPageChange={pagination.setPage}
              onPageSizeChange={pagination.setPageSize}
            />
          </div>
        )}
      </div>

      <ModuleConsoleFooter consoleRoute={routes.supplyAdminConsole} moduleLabel="ITaaS Inventory" />

      <LabelPrintModal
        open={printOpen}
        label={printLabel}
        onClose={() => setPrintOpen(false)}
      />
    </div>
  );
}

export function SupplyMedicinesArticlesPage() {
  return <SupplyArticlesPage depot="pharmacy" />;
}

export function SupplyConsumablesArticlesPage() {
  return <SupplyArticlesPage depot="consumables" />;
}
